import _ from 'lodash';
import {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ForwardedRef,
} from 'react';
import {
  handleMonitoringFrontendError,
  handleRequestFileUpload,
  clearApolloCache,
} from '@/shared/api';
import {
  fetchAIProcessingStatus,
  handleUpdateSummaryReport,
  selectChangeStripData,
  selectIsActiveTab,
  useReportStore,
} from '@/entities/report';
import {
  fetchHolterTasks,
  handleUpdateHolterProfile,
  callStartHolterStudyProcessing,
} from '@/entities/study';
import { handleUpdateBeatsAndEvents } from '@/entities/beat';
import { ANNOTATION_TYPE } from '@/shared/ui';
import {
  EMITTER_CONSTANTS,
  FRONT_END_ERRORS,
  AIRP_TAB,
  KEY_RECOIL,
  TAB_KEY,
} from '@/shared/config';
import {
  deleteDataState,
  getDataState,
  isValidHttpUrl,
  putFileToUrl,
  zeroPad,
  auth,
} from '@/shared/lib';
import {
  checkLimitStudyProcessing,
  convertToMomentJs,
  isHolterProcessingAvailable,
} from '@/shared/lib/aiUtils';
import { useEmitter } from '@/shared/lib/customHooks';
import { reportTabStatus } from '@/shared/lib/devLog';
import emitter from '@/shared/lib/eventEmitter';
import { toastrError, toastrSuccess } from '@/shared/lib/toastNotification';
import { logError } from '@/shared/lib/apiHelpers';
import { calculateHrInfoByDate } from '@/entities/heart-rate';
import { checkIsValidDeviceStrip, formatAiEventChangeByStrips } from './handler';
import type { StripsManagementV2Handle, StripsManagementV2Props } from './index';

/**
 * Drives the Strips Management report tab: ECG disclosure selection, AI and device strip edits, saving, and Holter AI processing startup. Wires report-store state under the tab-3 key, seeds the default ECG disclosure from the holter profile, starts Holter study processing once a socket id exists, gates tab entry on outstanding AI requests, and exposes isChangedData, onClickSaveReport and resetData through the forwarded ref.
 *
 * @param props - Tab inputs: the loaded report data, ECG data map, timezone offset, active-tab id and the isActive, isNotEdit and needStartedAI flags.
 * @param ref - Forwarded ref the parent uses to query dirty state, trigger a save, or reset the tab.
 * @returns An object carrying keyRecoil, isStartedAiProcessing, isShowTabHeader and isTabActive for the tab shell.
 */
const useStripsManagementV2 = (
  {
    activeTab,
    isActive,
    needStartedAI,
    isNotEdit,
    reportData,
    ecgDataMap,
    timezoneOffset,
  }: StripsManagementV2Props,
  ref: ForwardedRef<StripsManagementV2Handle>,
) => {
  const keyRecoil = KEY_RECOIL.STRIP_MANAGEMENT;
  const ecgDisclosure = useReportStore((s) => s.selectedEcgDisclosure);
  const setSelectedEcgDisclosure = useReportStore((s) => s.actions.setSelectedEcgDisclosure);
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Moves the ECG viewer to a given timestamp for this tab. Delegates to the store jump-date action with the tab-3 key bound, so callers pass only the target value.
   *
   * @param v - The timestamp to jump the ECG view to.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const isShowTabHeader = useReportStore((s) => s.isShowTabHeader);
  const setActionSnapshot = useReportStore((s) => s.actions.setActionSnapshot);
  const setSavingStripManagement = useReportStore((s) => s.actions.setIsSavingStripmanagement);
  const changeStripData = useReportStore(selectChangeStripData(keyRecoil));
  const setChangeStripDataAction = useReportStore((s) => s.actions.setChangeStripData);
  /**
   * Applies a change to the AI strip edit list held for this tab. Delegates to the store action with the tab-3 key already bound, so callers pass only the new value or updater.
   *
   * @param v - The replacement strip change list, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setChangeStripData = (v: Parameters<typeof setChangeStripDataAction>[1]) =>
    setChangeStripDataAction(keyRecoil, v);
  const deviceChangeStripData = useReportStore(selectChangeStripData(KEY_RECOIL.DEVICE_MANAGEMENT));
  /**
   * Applies a change to the device strip edit list shown alongside the AI strips. Delegates to the same store action bound to the tab-4 key, which is where device strip changes are kept.
   *
   * @param v - The replacement device strip change list, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setDeviceChangeStripData = (v: Parameters<typeof setChangeStripDataAction>[1]) =>
    setChangeStripDataAction(KEY_RECOIL.DEVICE_MANAGEMENT, v);
  const defaultSelectedEcgDisclosure = useRef<any>();
  const ecgDisclosureChange = useReportStore((s) => s.ecgDisclosureChange);
  const setEcgDisclosureChange = useReportStore((s) => s.actions.setEcgDisclosureChange);
  /**
   * Reads the pending ECG disclosure edit straight from the report store, outside React's subscription. Calls getState so callers inside effects and callbacks see the current value rather than the one captured at render time.
   *
   * @returns The pending ECG disclosure change currently held in the report store.
   */
  const getEcgDisclosureChange = () => useReportStore.getState().ecgDisclosureChange;
  const isStartedAiProcessing = useReportStore((s) => s.isStartedAiProcessing);
  const setIsStartedAiProcessing = useReportStore((s) => s.actions.setIsStartedAiProcessing);
  const setLimitProcessingStudy = useReportStore((s) => s.actions.setLimitProcessingStudy);
  const setIsOpenProcessingLimit = useReportStore((s) => s.actions.setIsOpenProcessingLimit);
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));
  const setIsActiveTabAction = useReportStore((s) => s.actions.setIsActiveTab);
  /**
   * Marks the strips-management tab as the active one in the report store. Delegates to the store action with the tab-3 key bound, so callers pass only the flag or updater.
   *
   * @param v - The new active state, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setIsActiveTab = (v: Parameters<typeof setIsActiveTabAction>[1]) =>
    setIsActiveTabAction(keyRecoil, v);
  const [isTabActive, setIsTabActive] = useState(false);

  const hasCallStart = useRef<any>();
  // reportInfoState (Recoil, Task 7) is typed {} | IReportInfo (atom default is {}) - pre-existing gap.
  const { studyId, profileId, studyFid } = useReportStore((s) => s.reportInfo) as any;

  const selectedEcgDisclosure = useMemo(() => {
    if (!_.isEmpty(ecgDisclosureChange)) return ecgDisclosureChange;
    return ecgDisclosure;
  }, [ecgDisclosure, ecgDisclosureChange]);

  /**
   * Finalizes a successful strip save by announcing it and clearing local edit state. Emits the update-event-success signal tagged for the strips-management tab, then empties both the AI and the device strip change lists.
   *
   * @param updatedType - Flags describing whether device events, AI events, or both were updated.
   * @returns Nothing.
   */
  const updateEventSuccess = (updatedType: any) => {
    emitter.emit(EMITTER_CONSTANTS.UPDATE_EVENT_SUCCESS, { tab: 'stripsManagement', updatedType });
    setChangeStripData([]);
    setDeviceChangeStripData([]);
  };

  /**
   * Persists every pending strip edit made in the Strips Management tab. Builds one update payload from the device strip changes and another from the AI strip changes, uploads locally drawn SVG strips to presigned URLs and swaps the returned links in, fires the summary-report and beats-and-events mutations plus a holter-profile update when the ECG disclosure moved, clears the Apollo cache, then reports invalid device strips to frontend monitoring.
   *
   * @returns A promise resolving once every update mutation has settled and the saving flag is cleared.
   */
  const handleSaveReport = async () => {
    try {
      const promises: any[] = [];
      const updatedDeviceEvents: any[] = [];
      const tempDeviceStripUpdate: any[] = [];
      const tempDeviceArrImgUpload: any[] = [];
      const deviceEventChanges = [...(deviceChangeStripData || [])];
      const deviceEventErrors: any[] = [];
      _.forEach(deviceEventChanges, (d) => {
        const { isIncludedToReport, isIncludedStrip, comment, isValid, stripImgs } =
          d.originalInfo || {};
        const isValidDeviceStrip = checkIsValidDeviceStrip(d);
        if (!isValidDeviceStrip && d.isUpdateStripImage) {
          deviceEventErrors.push({
            eventId: d?.id,
            friendlyId: d?.friendlyId,
            message: 'This strip is not valid. Please check the strip again.',
            originalHrInfo: d?.originalInfo?.hrInfo,
            updatedHrInfo: d?.hrInfo,
          });
        }
        updatedDeviceEvents.push({
          eventType: d.isIncludedStrip ? d.type : undefined,
          eventId: d.id,
          isIncluded:
            d.isIncludedToReport === isIncludedToReport ? undefined : d.isIncludedToReport,
          isSelected: d.isIncludedStrip === isIncludedStrip ? undefined : d.isIncludedStrip,
          isValid: d.isValid === isValid ? undefined : d.isValid,
          comments: comment === d.comment ? undefined : [d.comment || ''],
          images: _.isEqual(d.stripImgs, stripImgs) ? undefined : d.stripImgs,
          hrInfo: _.isEqual(d.stripImgs, stripImgs) ? undefined : d.hrInfo,
          friendlyId: d.friendlyId,
        });
        if (d?.stripImgs?.length && !isValidHttpUrl(d.stripImgs[0])) {
          tempDeviceStripUpdate.push({
            id: d.id,
            imagesCount: d.stripImgs.length,
          });
          tempDeviceArrImgUpload.push(...d.stripImgs);
        }
      });
      const updatedAiEvents: any[] = [];
      const aiStripChanges = [...(changeStripData || [])];
      const aiEventChanges = formatAiEventChangeByStrips(aiStripChanges);
      const tempDataStripUpdate: any[] = [];
      const tempArrImgUpload: any[] = [];
      _.forEach(aiEventChanges, (d) => {
        if (d.isDeletedEvent) {
          updatedAiEvents.push({
            id: d.id,
            isIncludedToReport: false,
            isDeleted: true,
          });
        } else {
          if (d.strips?.length > 0) {
            _.forEach(d.strips, (strip) => {
              if (strip?.images?.length && !isValidHttpUrl(strip.images[0])) {
                tempDataStripUpdate.push({
                  id: d.id,
                  stripId: strip.id,
                  imagesCount: strip.images.length,
                });
                tempArrImgUpload.push(...strip.images);
              }
            });
          }
          updatedAiEvents.push({
            id: d.id,
            isIncludedToReport: d.isIncludedToReport,
            title: d.title || undefined,
            strips: _.isEmpty(d.strips) ? undefined : _.cloneDeep(d.strips),
          });
        }
      });
      if (!_.isEmpty(updatedAiEvents) || !_.isEmpty(updatedDeviceEvents)) {
        setSavingStripManagement(true);
      }
      if (updatedDeviceEvents.length && tempDeviceArrImgUpload.length) {
        const uploadFileUrls = await handleRequestFileUpload('svg', tempDeviceArrImgUpload.length);
        if (uploadFileUrls?.length) {
          const putFileToUrlPromises = _.map(uploadFileUrls, (uploadFileUrl, index) =>
            putFileToUrl(uploadFileUrl, tempDeviceArrImgUpload[index], false),
          );
          await Promise.all(putFileToUrlPromises);
          _.forEach(tempDeviceStripUpdate, (data) => {
            const foundUpdatedDeviceEvent = _.find(
              updatedDeviceEvents,
              (x) => x.eventId === data.id,
            );
            const images = [];
            for (let i = 0; i < data.imagesCount; i += 1) {
              images.push(uploadFileUrls.shift());
            }
            _.assign(foundUpdatedDeviceEvent, { images });
          });
        }
      }
      if (updatedAiEvents.length && tempArrImgUpload.length) {
        const uploadFileUrls = await handleRequestFileUpload('svg', tempArrImgUpload.length);
        if (uploadFileUrls?.length) {
          const putFileToUrlPromises = _.map(uploadFileUrls, (uploadFileUrl, index) =>
            putFileToUrl(uploadFileUrl, tempArrImgUpload[index], false),
          );
          await Promise.all(putFileToUrlPromises);
          _.forEach(tempDataStripUpdate, (data) => {
            const foundUpdatedAiEvent = _.find(updatedAiEvents, (x) => x.id === data.id);
            const findStrip = _.find(foundUpdatedAiEvent.strips, (obj) => obj.id === data.stripId);
            const findDraw = _.find(
              findStrip.draw,
              (obj) => obj.type === ANNOTATION_TYPE.SAMPLE_MARK,
            );
            if (findDraw) {
              findStrip.start = findDraw.startTime;
              findStrip.stop = findDraw.endTime;
            }
            for (let i = 0; i < data.imagesCount; i += 1) {
              if (!_.isArray(findStrip.images)) {
                findStrip.images = [uploadFileUrls.shift()];
              } else {
                findStrip.images[i] = uploadFileUrls.shift();
              }
            }
          });
        }
      }
      if (!_.isEmpty(updatedDeviceEvents)) {
        const input = {
          studyId: reportData?.study?.id,
          updatedEvents: updatedDeviceEvents,
          reportId: reportData?.report?.id,
        };
        promises.push(handleUpdateSummaryReport(input));
      }
      if (!_.isEmpty(updatedAiEvents)) {
        const requestId = `requests:${profileId}:${Date.now()}`;
        const input = {
          studyId: reportData?.study?.id,
          profileId: reportData?.holterProfile?.id,
          tab: AIRP_TAB.STRIPS_MANAGEMENT,
          requestId,
          eventData: {
            updateEvents: updatedAiEvents,
          },
        };
        //* Update duration (start-stop), event type thì BE mới gửi qua AI
        promises.push(handleUpdateBeatsAndEvents(input));
      }
      if (!_.isEqual(selectedEcgDisclosure, defaultSelectedEcgDisclosure.current)) {
        const updateHolterProfileInput = {
          id: reportData?.holterProfile?.id,
          ecgDisclosure: {
            start: selectedEcgDisclosure.start,
            stop: selectedEcgDisclosure.stop,
            channel: selectedEcgDisclosure.channel,
          },
        };
        promises.push(handleUpdateHolterProfile(updateHolterProfileInput));
        deleteDataState('ecgDisclosureChangeState');
      }
      await Promise.all(promises);
      if (!_.isEmpty(updatedDeviceEvents) || !_.isEmpty(updatedAiEvents)) {
        await clearApolloCache();
        const updatedType = {
          isUpdateDevice: !_.isEmpty(updatedDeviceEvents),
          isUpdateAi: !_.isEmpty(updatedAiEvents),
        };
        updateEventSuccess(updatedType);
        toastrSuccess('Save event strips success', 'Success');
      }
      setEcgDisclosureChange((prev) => {
        if (!prev) return prev;
        return undefined;
      });
      if (!_.isEmpty(deviceEventErrors)) {
        handleMonitoringFrontendError({
          exception: JSON.stringify(deviceEventErrors),
          type: FRONT_END_ERRORS.UPDATE_DEVICE_EVENT_ERROR,
          userId: auth.userId(),
        });
      }
    } catch (error) {
      logError('Failed to save report: ', error);
      toastrError(error.message, 'Error');
      setTimeout(() => {
        emitter.emit(EMITTER_CONSTANTS.AI_LOADING, { isLoading: false });
      }, 100);
    } finally {
      setSavingStripManagement(false);
    }
  };

  /**
   * Reports whether the strips-management tab holds unsaved edits. Returns true when the AI strip change list, the device strip change list, or the pending ECG disclosure change is non-empty.
   *
   * @returns True when any AI strip, device strip, or ECG disclosure change is pending.
   */
  const checkChangedData = () =>
    !!changeStripData?.length || !!deviceChangeStripData?.length || !_.isEmpty(ecgDisclosureChange);

  /**
   * Kicks off AI Holter processing for the current study profile. Broadcasts the profile as pending, calls the start mutation, refetches holter tasks to refresh the pending list, opens the simultaneous-study limit modal with the offending study fids when the backend reports the limit was hit, otherwise records that start was called, warns when no processing slot is available, and flags AI processing as started once the backend is ready.
   *
   * @returns A promise resolving once processing has been requested and the pending task list refreshed.
   */
  const startHolterStudyProcessing = async () => {
    try {
      emitter.emit(EMITTER_CONSTANTS.UPDATE_PENDING_PROFILES, [{ id: profileId }]);
      const result = await callStartHolterStudyProcessing(studyId, profileId);
      const holterTasks = await fetchHolterTasks();
      emitter.emit(EMITTER_CONSTANTS.UPDATE_PENDING_PROFILES, holterTasks?.pendingProfiles || []);
      if (checkLimitStudyProcessing(result)) {
        setLimitProcessingStudy({
          isOpenModal: true,
          limitCount: result?.simultaneousStudyLimit,
          studyFids: _.map(result?.processingStudyFids, (x: any) => zeroPad(x)),
        });
        return;
      }
      hasCallStart.current = true;
      if (!isHolterProcessingAvailable(holterTasks, studyFid)) {
        setIsOpenProcessingLimit(true);
      }
      if (result?.isReady) {
        setIsStartedAiProcessing(true);
      }
    } catch (error) {
      logError('Failed to start holter study processing: ', error);
    }
  };

  /**
   * Defers the Holter processing start until the socket connection has an id. Re-schedules itself every 500 ms while auth reports no socket id, since the backend pushes progress over that socket, then calls the start routine.
   *
   * @returns Nothing.
   */
  const startHolterStudyProcessingByRetry = () => {
    const socketId = auth.getSocketId();
    console.log('[StripsManagementV2] startHolterStudyProcessingByRetry:socketId', { socketId });
    if (!socketId) {
      setTimeout(startHolterStudyProcessingByRetry, 500);
      return;
    }
    startHolterStudyProcessing();
  };

  useEffect(() => {
    const isActive = activeTab === TAB_KEY.STRIPS_MANAGEMENT;
    setIsTabActive(false);
    setIsActiveTab(isActive);
  }, [activeTab]);

  useEffect(() => {
    reportTabStatus(keyRecoil, {
      isActiveTab: isActive,
      isStartedAiProcessing,
      hasCallStart: !!hasCallStart.current,
    });
    if (isActive && !isStartedAiProcessing && !hasCallStart.current && needStartedAI) {
      startHolterStudyProcessingByRetry();
    }
  }, [isActive, isStartedAiProcessing, needStartedAI]);

  useImperativeHandle(ref, () => ({
    /**
     * Exposes the tab's dirty state to the parent report shell through the imperative handle. Combines the strip and ECG disclosure change check with the read-only flag so a non-editable tab never reports pending changes.
     *
     * @returns True when the tab has unsaved strip or ECG disclosure changes and editing is allowed.
     */
    isChangedData() {
      return checkChangedData() && !isNotEdit;
    },
    onClickSaveReport: async () => {
      if (!checkChangedData() && !isNotEdit) return;
      emitter.emit(EMITTER_CONSTANTS.AI_LOADING, { isLoading: true, isSaving: true });
      localStorage.setItem(`ai_saving-${profileId}`, true as any); // pre-existing bug: Storage.setItem requires a string, not boolean
      await handleSaveReport();
      setTimeout(() => {
        emitter.emit(EMITTER_CONSTANTS.AI_LOADING, { isLoading: false });
      }, 100);
    },
    /**
     * Discards every unsaved edit in the tab and restores the last loaded state. Empties the AI and device strip change lists, re-applies a clone of the baseline ECG disclosure captured at load, and clears the pending disclosure change.
     *
     * @returns Nothing.
     */
    resetData() {
      setChangeStripData([]);
      setDeviceChangeStripData([]);
      setSelectedEcgDisclosure(_.cloneDeep(defaultSelectedEcgDisclosure.current));
      setEcgDisclosureChange((prev) => {
        if (!prev) return prev;
        return undefined;
      });
    },
  }));
  /**
   * Seeds the tab with the ECG disclosure stored on the holter profile. Clones that disclosure, enriches it with heart-rate info computed over the ECG data map between its start and stop moments, keeps it as the reset baseline, and either jumps to a previously persisted unsaved disclosure or applies the profile one and clears any stale pending change.
   *
   * @returns A promise resolving once the default ECG disclosure has been computed and applied.
   */
  const setInitialData = async () => {
    if (!_.isEmpty(reportData?.holterProfile?.ecgDisclosure)) {
      const clonedSelectedEcgDisclosure = _.cloneDeep(reportData.holterProfile.ecgDisclosure);
      const hrInfo = calculateHrInfoByDate({
        ecgDataMap,
        start: convertToMomentJs(clonedSelectedEcgDisclosure?.start, timezoneOffset),
        stop: convertToMomentJs(clonedSelectedEcgDisclosure?.stop, timezoneOffset),
      });
      _.assign(clonedSelectedEcgDisclosure, hrInfo);
      defaultSelectedEcgDisclosure.current = clonedSelectedEcgDisclosure;
      const saved = await getDataState('ecgDisclosureChangeState');
      if (!_.isEmpty(saved?.value)) {
        setTimeout(() => {
          setJumpDate(saved?.value?.start);
        }, 100);
      } else {
        setSelectedEcgDisclosure(clonedSelectedEcgDisclosure);
        setJumpDate(clonedSelectedEcgDisclosure?.start);
        if (getEcgDisclosureChange()) {
          setEcgDisclosureChange(undefined);
        }
      }
    }
  };

  useEffect(() => {
    setInitialData();
  }, [reportData?.holterProfile?.ecgDisclosure]);

  /**
   * Decides whether the strips-management tab may be entered while AI processing requests for this study profile are still outstanding. Reads the cached requestId list for the profile and study from localStorage, short-circuits to active when it is empty or already owned by this tab, otherwise polls the AI processing status of every pending requestId, drops the finished ones from storage, and flips the tab-active flag from the result, falling back to inactive on error.
   *
   * @returns A promise resolving once the pending AI requests have been polled and the tab-active flag updated.
   */
  const checkCanSwitchTab = async () => {
    try {
      const keyStorage = `requestId-${profileId}-${studyFid}`;
      const storedResult = localStorage.getItem(keyStorage);
      const requestData: any = JSON.parse(storedResult as any);
      if (_.isEmpty(requestData)) return setIsTabActive(true);
      const { type, requestId } = requestData?.[0] || {};
      if (!requestId || type === keyRecoil) return setIsTabActive(true);
      const isProcessing = localStorage.getItem(`ai_process-${profileId}`);
      if (JSON.parse(isProcessing as any)) return setIsTabActive(true);
      const promisses: any[] = [];
      setIsTabActive(false);
      _.forEach(requestData, (x) => {
        if (x.requestId) {
          promisses.push(fetchAIProcessingStatus(x.requestId, 'switchTab'));
        }
      });
      const result = await Promise.all(promisses);
      let canSwitchTab = false;
      const updateSuccess: any[] = [];
      _.forEach(result, (x, i) => {
        if (x === true) {
          canSwitchTab = true;
          updateSuccess.push(requestData?.[i]);
        }
      });
      _.remove(
        requestData,
        (x: any) => !!_.find(updateSuccess, (y: any) => y.requestId === x.requestId),
      );
      localStorage.setItem(keyStorage, JSON.stringify(requestData));

      if (canSwitchTab) {
        localStorage.setItem(`ai_process-${profileId}`, JSON.stringify(canSwitchTab));
      }

      setIsTabActive(canSwitchTab);
    } catch (error) {
      console.log(error);
      setIsTabActive(false);
    }
    return null;
  };

  useEffect(() => {
    if (isActiveTab) {
      checkCanSwitchTab();
      setInitialData();
    }
  }, [isActiveTab]);

  useEmitter(
    EMITTER_CONSTANTS.AI_STOP_PROCESSING,
    async (msg: any) => {
      await clearApolloCache();
      if (isActiveTab && !_.isEmpty(msg)) {
        const { requestId } = msg || {};
        const keyStorage = `requestId-${profileId}-${studyFid}`;
        const storedResult = localStorage.getItem(keyStorage);
        const requestData: any = JSON.parse(storedResult as any);
        _.remove(requestData, (x: any) => x.requestId === requestId);
        localStorage.setItem(keyStorage, JSON.stringify(requestData));
        if (_.isEmpty(requestData)) {
          localStorage.setItem(`ai_process-${profileId}`, JSON.stringify(true));
          setIsTabActive(true);
        }
      }
    },
    [isActiveTab, profileId, studyFid],
  );

  useEffect(() => {
    if (isActive) {
      setTimeout(() => {
        setActionSnapshot(1);
      }, 200);
    }
  }, [isActive]);

  useEmitter(
    EMITTER_CONSTANTS.SOCKET_RECONNECTED,
    () => {
      if (isTabActive && !isStartedAiProcessing && needStartedAI) {
        startHolterStudyProcessingByRetry();
      }
    },
    [isTabActive, isStartedAiProcessing, needStartedAI],
  );

  return {
    keyRecoil,
    isStartedAiProcessing,
    isShowTabHeader,
    isTabActive,
  };
};

export default useStripsManagementV2;
